import React from "react";
import {
  Target, Heart, Flag, Trophy, UsersThree, Buildings, MapPin, Star, Code, Briefcase,
} from "@phosphor-icons/react";
import { useSeo } from "../lib/useSeo";
import { COMPANY } from "../lib/constants";
import { useLang } from "../lib/i18n";
import { Reveal, Stagger, StaggerItem, SectionBadge } from "../components/blocks";
import { Leadership } from "../components/Leadership";

const STR = {
  en: {
    heroBadge: "About Priyajit Systems",
    heroT1: "Building Intelligent Solutions.", heroT2: "Creating Real Impact.",
    heroSub: `${COMPANY.legal} is a technology company building AI-powered platforms that solve real problems — combining data, AI and human expertise across two products: Atikxzio CMH and Atikxzio HMS.`,
    valuesBadge: "What We Stand For",
    values: [
      { icon: Target, title: "Our Mission", text: "Empower individuals with clarity, direction and confidence through the perfect blend of psychology, technology and AI." },
      { icon: Heart, title: "Why We Do It", text: "Because every right career decision can transform a life, a family and a future." },
      { icon: Flag, title: "What Drives Us", text: "Purpose over profit. Impact over scale. People over everything." },
    ],
    foundersBadge: "Meet The Founders",
    foundersT1: "Building the future of ", foundersGrad: "career decisions.",
    quote: "“We combine technology, psychology and healthcare expertise to empower individuals with clarity, direction and confidence to build better careers and better lives.”",
    certsLabel: "Certifications", acadLabel: "Academics", linkedin: "Connect on LinkedIn",
    founders: [
      {
        name: "Ajit Kumar",
        title: "Founder & CEO",
        photo: "/founder-ajit.jpg",
        linkedin: "https://www.linkedin.com/in/ajit-rai-aj280295",
        tags: ["Entrepreneur", "Engineer", "Problem Solver"],
        bio: "8+ years of backend engineering experience building scalable, secure & high-performance systems. Ex-LTIMindtree and Ex-QuinStreet Inc., now building intelligent solutions with impact at Priyajit Systems.",
        highlights: [
          { icon: Code, text: "8+ Years Backend Engineering" },
          { icon: Briefcase, text: "Ex-LTIMindtree · Ex-QuinStreet Inc." },
        ],
        certs: ["Oracle Certified Professional Java SE 8", "Microsoft Certified Azure Fundamentals (AZ-900)"],
        academics: ["B.Tech – Computer Science", "MBA – IT and Project Management", "MA – Psychology"],
      },
      {
        name: "Dr. Priyangu Rai",
        title: "COO & MD",
        photo: "/founder-priyangu.jpg",
        tags: ["Physiotherapist", "Clinical Expert", "People First"],
        bio: "BPT, MPT from RGUHS and Member of MIAP. Ex-Consultant at Manipal Hospital, Pune and Ex-ESI Hospital, Bangalore. Owner of Priyajit Ortho Neuro Rehabilitation Center, Gorakhpur, with 4+ years of clinical experience.",
        highlights: [
          { icon: Buildings, text: "Ex – Manipal Hospital, Pune · ESI Hospital, Bangalore" },
          { icon: Heart, text: "Owner – Priyajit Ortho Neuro Rehab Center" },
        ],
        certs: ["Certified Manual Therapist (CMT)", "Kinesio Taping Practitioner (KTP)", "Neurodynamic Technique Certification"],
        academics: ["BPT – Bachelor of Physiotherapy", "MPT in Musculoskeletal Sciences"],
      },
    ],
    stats: [
      { icon: Trophy, value: "15+", label: "Years Combined Experience" },
      { icon: Buildings, value: "2+", label: "Leading Hospitals" },
      { icon: UsersThree, value: "1000+", label: "Patients Transformed" },
      { icon: MapPin, value: "1", label: "Rehab Center, Gorakhpur" },
    ],
    banner: "This isn't just another platform. It's a mission to help millions make one of the most important decisions of their lives.",
  },
  hi: {
    heroBadge: "प्रियाजित सिस्टम्स के बारे में",
    heroT1: "इंटेलिजेंट सॉल्यूशंस का निर्माण।", heroT2: "वास्तविक प्रभाव की रचना।",
    heroSub: `${COMPANY.legal} एक टेक्नोलॉजी कंपनी है जो असली समस्याएँ हल करने वाले AI-आधारित प्लेटफ़ॉर्म बनाती है — डेटा, AI और मानवीय विशेषज्ञता को जोड़कर, दो प्रोडक्ट्स के ज़रिए: Atikxzio CMH और Atikxzio HMS।`,
    valuesBadge: "हमारे मूल्य",
    values: [
      { icon: Target, title: "हमारा मिशन", text: "साइकोलॉजी, टेक्नोलॉजी और AI के सही मेल से हर व्यक्ति को स्पष्टता, दिशा और आत्मविश्वास देना।" },
      { icon: Heart, title: "हम यह क्यों करते हैं", text: "क्योंकि एक सही करियर निर्णय एक ज़िंदगी, एक परिवार और एक भविष्य बदल सकता है।" },
      { icon: Flag, title: "हमें क्या प्रेरित करता है", text: "मुनाफ़े से पहले मक़सद। स्केल से पहले असर। सबसे पहले लोग।" },
    ],
    foundersBadge: "फ़ाउंडर्स से मिलिए",
    foundersT1: "गढ़ रहे हैं ", foundersGrad: "करियर निर्णयों का भविष्य।",
    quote: "“हम टेक्नोलॉजी, साइकोलॉजी और हेल्थकेयर विशेषज्ञता को जोड़कर हर व्यक्ति को बेहतर करियर और बेहतर ज़िंदगी बनाने की स्पष्टता, दिशा और आत्मविश्वास देते हैं।”",
    certsLabel: "प्रमाणपत्र", acadLabel: "शिक्षा", linkedin: "LinkedIn पर जुड़ें",
    founders: [
      {
        name: "अजित कुमार",
        title: "फ़ाउंडर व CEO",
        photo: "/founder-ajit.jpg",
        linkedin: "https://www.linkedin.com/in/ajit-rai-aj280295",
        tags: ["उद्यमी", "इंजीनियर", "समस्या-समाधानकर्ता"],
        bio: "स्केलेबल, सुरक्षित और हाई-परफ़ॉर्मेंस सिस्टम बनाने का 8+ वर्षों का बैकएंड इंजीनियरिंग अनुभव। Ex-LTIMindtree और Ex-QuinStreet Inc. — अब प्रियाजित सिस्टम्स में असरदार इंटेलिजेंट सॉल्यूशंस बना रहे हैं।",
        highlights: [
          { icon: Code, text: "8+ वर्षों की बैकएंड इंजीनियरिंग" },
          { icon: Briefcase, text: "Ex-LTIMindtree · Ex-QuinStreet Inc." },
        ],
        certs: ["Oracle Certified Professional Java SE 8", "Microsoft Certified Azure Fundamentals (AZ-900)"],
        academics: ["B.Tech – कंप्यूटर साइंस", "MBA – IT व प्रोजेक्ट मैनेजमेंट", "MA – साइकोलॉजी"],
      },
      {
        name: "डॉ. प्रियंगु राय",
        title: "COO व MD",
        photo: "/founder-priyangu.jpg",
        tags: ["फ़िज़ियोथेरेपिस्ट", "क्लिनिकल विशेषज्ञ", "पीपल फ़र्स्ट"],
        bio: "RGUHS से BPT, MPT और MIAP की सदस्य। मणिपाल हॉस्पिटल, पुणे और ESI हॉस्पिटल, बैंगलोर में पूर्व-कंसल्टेंट। गोरखपुर में प्रियाजित ऑर्थो न्यूरो रिहैबिलिटेशन सेंटर की मालिक, 4+ वर्षों के क्लिनिकल अनुभव के साथ।",
        highlights: [
          { icon: Buildings, text: "पूर्व – मणिपाल हॉस्पिटल, पुणे · ESI हॉस्पिटल, बैंगलोर" },
          { icon: Heart, text: "मालिक – प्रियाजित ऑर्थो न्यूरो रिहैब सेंटर" },
        ],
        certs: ["Certified Manual Therapist (CMT)", "Kinesio Taping Practitioner (KTP)", "Neurodynamic Technique Certification"],
        academics: ["BPT – बैचलर ऑफ़ फ़िज़ियोथेरेपी", "MPT – मस्कुलोस्केलेटल साइंसेज़"],
      },
    ],
    stats: [
      { icon: Trophy, value: "15+", label: "वर्षों का संयुक्त अनुभव" },
      { icon: Buildings, value: "2+", label: "प्रमुख अस्पताल" },
      { icon: UsersThree, value: "1000+", label: "मरीज़ों की ज़िंदगी बदली" },
      { icon: MapPin, value: "1", label: "रिहैब सेंटर, गोरखपुर" },
    ],
    banner: "यह सिर्फ़ एक और प्लेटफ़ॉर्म नहीं है। यह लाखों लोगों को उनकी ज़िंदगी का सबसे अहम फ़ैसला लेने में मदद करने का मिशन है।",
  },
};

export default function About() {
  const { lang } = useLang();
  const s = STR[lang];

  useSeo({
    title: "About Us | Priyajit Systems Private Limited",
    description: "Meet the founders of Priyajit Systems — Ajit Kumar (Founder & CEO) and Dr. Priyangu Rai (COO & MD). Technology, psychology and healthcare expertise building for real impact.",
    image: "/og-image.jpg",
  });

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-navy-deep grain pt-36 pb-24 lg:pt-44 lg:pb-28" data-testid="about-hero">
        <div aria-hidden className="absolute inset-0 bg-grid opacity-50" />
        <div aria-hidden className="absolute -right-32 top-10 h-96 w-96 rounded-full bg-electric/20 blur-[110px]" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <div className="text-slate-200"><SectionBadge number="00" label={s.heroBadge} tone="light" /></div>
          <h1 className="mt-6 max-w-4xl font-display text-5xl font-black leading-[1.08] tracking-tighter text-white sm:text-6xl lg:text-7xl">
            {s.heroT1}<br /><span className="text-gradient-electric">{s.heroT2}</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-300">{s.heroSub}</p>
          <p className="mt-4 text-sm font-bold uppercase tracking-[0.3em] text-electric-light">{COMPANY.pillars}</p>
        </div>
      </section>

      {/* Mission / values */}
      <section className="bg-[#F8FAFC] py-24 lg:py-28" data-testid="about-values">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><SectionBadge number="01" label={s.valuesBadge} /></Reveal>
          <Stagger className="mt-12 grid gap-6 md:grid-cols-3">
            {s.values.map((v, i) => (
              <StaggerItem key={i}>
                <div className="h-full rounded-3xl border border-navy/[0.06] bg-white p-8 shadow-[0_8px_32px_rgba(30,127,240,0.06)]" data-testid={`value-${i}`}>
                  <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-electric/10 text-electric"><v.icon size={26} weight="duotone" /></div>
                  <h3 className="font-display text-xl font-bold text-navy">{v.title}</h3>
                  <p className="mt-3 leading-relaxed text-slate-600">{v.text}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Founders */}
      <section className="bg-white py-24 lg:py-32" data-testid="founders-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><SectionBadge number="02" label={s.foundersBadge} /></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 max-w-3xl font-display text-4xl font-black tracking-tight text-navy sm:text-5xl">
              {s.foundersT1}<span className="text-gradient-blue">{s.foundersGrad}</span>
            </h2>
            <p className="mt-5 max-w-2xl text-lg italic leading-relaxed text-slate-600">{s.quote}</p>
          </Reveal>

          <div className="mt-16">
            <Leadership lang={lang} heading={false} storyLink={false} />
          </div>

          {/* Stats */}
          <div className="mt-16 grid gap-4 rounded-[2rem] bg-navy p-8 sm:grid-cols-2 lg:grid-cols-4 lg:p-10">
            {s.stats.map((st, i) => (
              <div key={i} className="text-center" data-testid={`about-stat-${i}`}>
                <st.icon size={30} weight="duotone" className="mx-auto text-electric-light" />
                <p className="mt-3 font-display text-4xl font-black text-white">{st.value}</p>
                <p className="mt-1 text-sm text-slate-400">{st.label}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 flex items-center gap-4 rounded-2xl bg-gradient-to-r from-navy to-electric p-6 text-white">
            <Star size={28} weight="fill" className="shrink-0" />
            <p className="font-display text-lg font-bold">{s.banner}</p>
          </div>
        </div>
      </section>
    </div>
  );
}
